import React, { useMemo, useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { Activity, TrendingDown, TrendingUp, Zap } from 'lucide-react';

function computeIMC(peso, tallaCm) {
  if (!peso || !tallaCm) return null;
  const m = tallaCm / 100;
  const imc = peso / (m * m);
  return Math.round(imc * 10) / 10;
}

function imcCategory(imc) {
  if (!imc) return { label: '—', color: '#a78bfa', desc: '' };
  if (imc < 18.5) return { label: 'Bajo peso', color: '#60a5fa', desc: 'Por debajo del rango saludable' };
  if (imc < 25) return { label: 'Peso saludable', color: '#34d399', desc: 'Dentro del rango ideal' };
  if (imc < 30) return { label: 'Sobrepeso', color: '#fbbf24', desc: 'Por encima del rango ideal' };
  if (imc < 35) return { label: 'Obesidad grado I', color: '#fb923c', desc: 'Requiere atención profesional' };
  if (imc < 40) return { label: 'Obesidad grado II', color: '#f87171', desc: 'Requiere intervención' };
  return { label: 'Obesidad grado III', color: '#ef4444', desc: 'Atención médica prioritaria' };
}

// Animated count-up hook
function useCountUp(target, duration = 1800) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (target == null) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(target * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function imcToBarPct(imc) {
  if (!imc) return 0;
  // Piecewise linear mapping that matches the visual labels (evenly distributed at 0/25/50/75/100%)
  // 18.5 → 0%  · 25 → 25%  · 30 → 50%  · 35 → 75%  · 40+ → 100%
  if (imc <= 18.5) return 0;
  if (imc < 25) return ((imc - 18.5) / (25 - 18.5)) * 25;
  if (imc < 30) return 25 + ((imc - 25) / 5) * 25;
  if (imc < 35) return 50 + ((imc - 30) / 5) * 25;
  if (imc < 40) return 75 + ((imc - 35) / 5) * 25;
  return 100;
}

export default function IMCPanel({ answers }) {
  const datos = answers?.datos || {};
  const peso = Number(datos.peso) || 0;
  const talla = Number(datos.talla) || 0;
  const imc = useMemo(() => computeIMC(peso, talla), [peso, talla]);
  const cat = imcCategory(imc);

  const animatedImc = useCountUp(imc || 0, 2200);
  const animatedPct = useCountUp(imcToBarPct(imc), 2400);
  const [showCat, setShowCat] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowCat(true), 1700);
    return () => clearTimeout(t);
  }, [imc]);

  // Projection: 4 weeks
  // Reduction scales with BMI: low BMI ~ -15%, high BMI 35+ ~ -35%
  const projection = useMemo(() => {
    const safeImc = imc || 22;
    const norm = Math.min(Math.max((safeImc - 20) / 15, 0), 1); // 0..1 across BMI 20 to 35+
    const reducFinal = -(15 + norm * 20); // -15% to -35%
    // Tonificación + Definición: assume good gains independent of BMI
    return [
      { week: 'Sesión 0', reduccion: 0, tonificacion: 0, definicion: 0 },
      { week: 'Sesión 1', reduccion: reducFinal * 0.30, tonificacion: 12, definicion: 8 },
      { week: 'Semana 2', reduccion: reducFinal * 0.58, tonificacion: 24, definicion: 18 },
      { week: 'Semana 3', reduccion: reducFinal * 0.82, tonificacion: 38, definicion: 31 },
      { week: 'Semana 4', reduccion: reducFinal, tonificacion: 52, definicion: 46 },
    ];
  }, [imc]);

  return (
    <div className="space-y-5 mb-6" data-testid="imc-panel">
      {/* IMC Card */}
      <div className="quiz-dark p-5 sm:p-6 relative overflow-hidden" data-testid="imc-card">
        {/* Animated glow background */}
        <div
          className="absolute -top-20 -right-20 w-60 h-60 rounded-full blur-3xl opacity-40 imc-glow-pulse"
          style={{ background: cat.color }}
        ></div>

        <div className="flex items-center gap-3 mb-4 relative z-10">
          <div className="w-10 h-10 rounded-full bg-purple-500/15 border border-purple-400/40 flex items-center justify-center neon-pulse">
            <Activity className="w-5 h-5 text-fuchsia-300" />
          </div>
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-purple-300/80 font-bold">Tu Diagnóstico Inicial</p>
            <p className="font-display text-lg font-bold text-white">Índice de Masa Corporal</p>
          </div>
        </div>

        {imc ? (
          <div className="relative z-10">
            <div className="flex items-end justify-between mb-4 gap-4">
              <div className="imc-number-wrap">
                <p
                  className="font-display font-bold leading-none imc-number"
                  style={{
                    color: cat.color,
                    textShadow: `0 0 30px ${cat.color}90, 0 0 60px ${cat.color}55`,
                    fontSize: 'clamp(56px, 12vw, 80px)',
                  }}
                >
                  {animatedImc.toFixed(1)}
                </p>
                <p className="text-xs text-purple-300/70 mt-1 tracking-wider uppercase">kg/m²</p>
              </div>
              <div
                className={`text-right max-w-[55%] transition-all duration-700 ${showCat ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              >
                <span
                  className="inline-block px-3 py-1.5 rounded-full text-xs font-bold tracking-wider uppercase imc-badge"
                  style={{
                    background: `${cat.color}22`,
                    color: cat.color,
                    border: `1.5px solid ${cat.color}66`,
                    boxShadow: showCat ? `0 0 24px ${cat.color}40` : 'none',
                  }}
                >
                  {cat.label}
                </span>
                <p className="text-purple-200/70 text-xs mt-1.5 leading-snug">{cat.desc}</p>
              </div>
            </div>

            {/* IMC scale bar */}
            <div
              className="relative h-2.5 rounded-full overflow-hidden mb-2"
              style={{
                background: 'linear-gradient(90deg, #60a5fa 0%, #34d399 25%, #fbbf24 50%, #fb923c 65%, #f87171 80%, #ef4444 100%)',
              }}
            >
              {/* moving marker */}
              <div
                className="absolute top-1/2 w-1 h-6 bg-white shadow-2xl"
                style={{
                  left: `${animatedPct}%`,
                  transform: 'translate(-50%, -50%)',
                  boxShadow: `0 0 12px #fff, 0 0 20px ${cat.color}`,
                  borderRadius: 2,
                  transition: 'box-shadow .3s',
                }}
              />
              {/* pulsing dot */}
              <div
                className="absolute top-1/2 w-4 h-4 rounded-full imc-marker-pulse"
                style={{
                  left: `${animatedPct}%`,
                  transform: 'translate(-50%, -50%)',
                  background: '#fff',
                  border: `2px solid ${cat.color}`,
                }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-purple-300/60 font-mono">
              <span>18.5</span><span>25</span><span>30</span><span>35</span><span>40+</span>
            </div>

            <p className="text-purple-200/70 text-xs mt-3 leading-relaxed">
              Basado en <strong className="text-white">{peso} kg</strong> y <strong className="text-white">{talla} cm</strong> que ingresaste.
            </p>
          </div>
        ) : (
          <p className="text-purple-300/70 text-sm">Completa tus datos en el paso anterior para ver tu IMC.</p>
        )}
      </div>

      {/* Projection Chart */}
      <div className="quiz-dark p-5 sm:p-6" data-testid="projection-chart">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-purple-500/15 border border-purple-400/40 flex items-center justify-center">
            <Zap className="w-5 h-5 text-fuchsia-300" />
          </div>
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-purple-300/80 font-bold">Tu Proyección</p>
            <p className="font-display text-lg font-bold text-white">Resultados estimados — 4 semanas</p>
          </div>
        </div>

        <div className="h-56 sm:h-64 -mx-2" data-testid="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={projection} margin={{ top: 10, right: 14, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="redG" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#f472b6" stopOpacity={1} />
                  <stop offset="100%" stopColor="#a855f7" stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#a855f733" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="week"
                tick={{ fill: '#c4b5fd', fontSize: 11 }}
                axisLine={{ stroke: '#7c3aed55' }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: '#c4b5fd', fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                unit="%"
              />
              <Tooltip
                contentStyle={{
                  background: 'rgba(20, 8, 32, 0.95)',
                  border: '1px solid rgba(217, 70, 239, 0.4)',
                  borderRadius: 12,
                  color: '#fff',
                  fontSize: 12,
                  padding: 10,
                  boxShadow: '0 10px 30px -5px rgba(217, 70, 239, 0.4)',
                }}
                labelStyle={{ color: '#f0abfc', fontWeight: 700, marginBottom: 4 }}
                formatter={(val, name) => {
                  const labels = {
                    reduccion: 'Reducción de grasa',
                    tonificacion: 'Tonificación',
                    definicion: 'Definición corporal',
                  };
                  return [`${val > 0 ? '+' : ''}${val.toFixed(1)}%`, labels[name] || name];
                }}
              />
              <Legend
                verticalAlign="top"
                height={32}
                iconType="circle"
                wrapperStyle={{ fontSize: 11, color: '#e9d5ff' }}
                formatter={(value) => {
                  const labels = {
                    reduccion: 'Reducción de grasa',
                    tonificacion: 'Tonificación',
                    definicion: 'Definición',
                  };
                  return <span style={{ color: '#e9d5ff' }}>{labels[value] || value}</span>;
                }}
              />
              <Line
                type="monotone"
                dataKey="reduccion"
                stroke="url(#redG)"
                strokeWidth={3}
                dot={{ fill: '#f472b6', stroke: '#fff', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#f472b6', stroke: '#fff', strokeWidth: 2 }}
                isAnimationActive
                animationDuration={3200}
                animationBegin={200}
                animationEasing="ease-out"
              />
              <Line
                type="monotone"
                dataKey="tonificacion"
                stroke="#34d399"
                strokeWidth={3}
                dot={{ fill: '#34d399', stroke: '#fff', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#34d399', stroke: '#fff', strokeWidth: 2 }}
                isAnimationActive
                animationDuration={3500}
                animationBegin={900}
                animationEasing="ease-out"
              />
              <Line
                type="monotone"
                dataKey="definicion"
                stroke="#60a5fa"
                strokeWidth={3}
                dot={{ fill: '#60a5fa', stroke: '#fff', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: '#60a5fa', stroke: '#fff', strokeWidth: 2 }}
                isAnimationActive
                animationDuration={3800}
                animationBegin={1600}
                animationEasing="ease-out"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-3">
          <Stat icon={<TrendingDown className="w-4 h-4" />} color="#f472b6" label="Reducción de grasa" val={`-${Math.abs(projection[4].reduccion).toFixed(1)}%`} delay={0} />
          <Stat icon={<TrendingUp className="w-4 h-4" />} color="#34d399" label="Tonificación" val={`+${projection[4].tonificacion}%`} delay={150} />
          <Stat icon={<TrendingUp className="w-4 h-4" />} color="#60a5fa" label="Definición" val={`+${projection[4].definicion}%`} delay={300} />
        </div>

        <p className="text-[11px] text-purple-300/55 mt-3 text-center italic leading-relaxed">
          *Resultados estimados con protocolo personalizado 2 sesiones/semana. Resultados reales se determinan en valoración.
        </p>
      </div>
    </div>
  );
}

function Stat({ icon, color, label, val, delay = 0 }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 2800 + delay);
    return () => clearTimeout(t);
  }, [delay]);
  return (
    <div
      className={`glass-card-sm p-2.5 text-center transition-all duration-700 ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
      style={{ boxShadow: show ? `0 0 24px ${color}30, inset 0 1px 0 ${color}20` : 'none' }}
    >
      <div className="flex items-center justify-center gap-1 mb-1" style={{ color }}>
        {icon}
        <span className="text-[10px] uppercase tracking-wider font-bold">{label}</span>
      </div>
      <p className="font-display text-base font-bold text-white">{val}</p>
    </div>
  );
}
